import {
  ArrowBack,
  DashboardCustomizeOutlined,
  Delete,
  Link as LinkIcon,
  ViewSidebar,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import ActionItemDialog from "./ActionItemDialog";
import ActionItemsTable from "./ActionItemsTable";
import TaskFormFields, { RecurrenceControl } from "./TaskFormFields";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { useTaskOverlay } from "../../utils/TaskOverlayContext";
import useCurrentUser, { guestCapabilities } from "../../utils/useCurrentUser";
import ActionItemTemplateDialog from "../dialogs/ActionItemTemplateDialog";
import useActionItems from "../../hooks/useActionItems";
import useTaskFieldPatcher from "../../hooks/useTaskFieldPatcher";

// Optional foreign keys must clear to null, not "", for the API to accept them.
const NULLABLE_FKS = ["division", "department", "assignee", "reviewer"];

// Fields flagged when empty. `project` is left out: on an existing task it is
// derived from the track and only scopes the picker, so it would flash red
// while the track list loads.
const REQUIRED_FIELDS = [
  "name",
  "assignee",
  "track",
  "department",
  "division",
  "category",
];

/**
 * Task detail surface, used both as a route page and inside the detail drawer.
 * Renders the same {@link TaskFormFields} and {@link ActionItemsTable} as the
 * create dialog, so every field sits in the same place on both.
 */
const TaskDetailsPage = ({ taskId: propTaskId }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showTaskAsDrawer } = useTaskOverlay();
  // Used as a route page (taskId from URL) or embedded in the detail drawer
  // (taskId passed as a prop).
  const taskId = propTaskId != null ? String(propTaskId) : params.taskId;
  const isDrawerView = propTaskId != null;

  const { currentWorkspace } = useWorkspace();
  const { user: currentUser } = useCurrentUser();
  const guestCaps = guestCapabilities(currentUser, currentWorkspace?.id);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);

  const [epics, setEpics] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Epic and project are not task columns — they scope the Track picker, the
  // same way they do in the create dialog.
  const [epicFilter, setEpicFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedActionItem, setSelectedActionItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleGoBack = () => {
    // Return to the page the user came from, falling back to the tasks list.
    if (window.history.length > 1) navigate(-1);
    else navigate("/all-task");
  };

  // Switch this task from the full-page route back into the side drawer,
  // overlaying whatever page the user came from.
  const handleShowAsDrawer = () => {
    showTaskAsDrawer(taskId);
    handleGoBack();
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/tasks/${taskId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers / contexts without the async clipboard API.
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setLinkCopied(true);
  };

  // Merges the PATCH response rather than replacing state with it: a reply
  // describes the task as of when its request was sent, so overwriting wholesale
  // discarded any field the user touched while it was in flight.
  const mergeResponseRef = useRef(null);
  const refetchTaskRef = useRef(null);
  const { patchField, mergeResponse } = useTaskFieldPatcher({
    taskId,
    workspaceId: currentWorkspace?.id,
    onPatched: (data) => setTask((prev) => mergeResponseRef.current(prev, data)),
    // A rejected save used to be swallowed into the console, so the field just
    // silently reverted on the next reload with no hint why. Show the server's
    // reason and resync the form.
    onError: (error) => {
      const data = error?.response?.data;
      setSaveError(
        data?.detail ||
          // Field errors (e.g. a division that doesn't match the track).
          (data && typeof data === "object"
            ? Object.values(data).flat().join(" ")
            : "") ||
          "That change could not be saved. The task has been reloaded."
      );
      refetchTaskRef.current?.();
    },
  });
  mergeResponseRef.current = mergeResponse;

  const {
    items: actionItems,
    setItems: setActionItems,
    addItem: handleAddActionItem,
    updateItem: handleActionItemUpdate,
    removeItem: removeActionItem,
    reload: reloadActionItems,
    clearInvalidTypes: clearInvalidActionItemTypes,
  } = useActionItems(taskId, { workspaceId: currentWorkspace?.id });

  // Workspace-level option lists.
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    const params = { workspace_id: currentWorkspace.id };
    Promise.all([
      api.get("epics/", { params }),
      api.get("tracks/", { params }),
      api.get("divisions/", { params }),
      api.get("departments/", { params }),
    ])
      .then(([epicsRes, tracksRes, divisionsRes, departmentsRes]) => {
        setEpics(epicsRes.data);
        setTracks(tracksRes.data);
        setDivisions(divisionsRes.data);
        setDepartments(departmentsRes.data);
      })
      .catch((error) => console.error("Error fetching task form options:", error));
  }, [currentWorkspace?.id]);

  // Projects, narrowed by the selected epic (same behaviour as the create dialog).
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    const params = { workspace_id: currentWorkspace.id };
    if (epicFilter) params.epic_id = epicFilter;
    api
      .get("/filtered-projects/", { params })
      .then((res) =>
        setProjects(Array.isArray(res.data) ? res.data : res.data?.payload || [])
      )
      .catch((error) => console.error("Error fetching projects:", error));
  }, [epicFilter, currentWorkspace?.id]);

  // Re-read the task from the API. Used on mount and to resync after a save is
  // rejected, so the form shows what was actually stored rather than the
  // optimistic value the server refused.
  const refetchTask = useCallback(async () => {
    if (!taskId || !currentWorkspace?.id) return;
    try {
      const { data } = await api.get(`tasks/${taskId}`, {
        params: { workspace_id: currentWorkspace.id },
      });
      setTask(data);
    } catch (error) {
      console.error("Error fetching task:", error);
    }
  }, [taskId, currentWorkspace?.id]);
  // Declared after the patcher above, which reaches it through this ref.
  refetchTaskRef.current = refetchTask;

  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        const [taskData, actionsData] = await Promise.all([
          api.get(`tasks/${taskId}`, {
            params: { workspace_id: currentWorkspace.id },
          }),
          api.get(`tasks/${taskId}/action-items/`, {
            params: { workspace_id: currentWorkspace.id },
          }),
        ]);
        setTask(taskData.data);
        setActionItems(actionsData.data.payload || []);
      } catch (error) {
        console.error("Error fetching task details:", error);
      }
      setLoading(false);
    };
    fetchTaskDetails();
  }, [taskId, currentWorkspace?.id, setActionItems]);

  // Seed the epic/project scoping from the task's own track, once per task.
  //
  // These used to re-seed whenever the filters were empty, which fought the
  // user: picking a different Epic deliberately clears the project, and the
  // effect immediately put the track's own project back, so the Project select
  // snapped to its old value.
  const scopeSeededRef = useRef(null);
  useEffect(() => {
    if (scopeSeededRef.current === taskId) return;
    if (!task?.track || tracks.length === 0 || projects.length === 0) return;

    const trackObj = tracks.find((t) => String(t.id) === String(task.track));
    if (!trackObj?.project) return;
    scopeSeededRef.current = taskId;

    setProjectFilter(String(trackObj.project));
    const projectObj = projects.find(
      (p) => String(p.id) === String(trackObj.project)
    );
    if (projectObj?.primary_epic) setEpicFilter(String(projectObj.primary_epic));
  }, [taskId, task?.track, tracks, projects]);

  // `/tasks/:id?action_item=<id>` (e.g. from the Time Logs page) opens that
  // action item's dialog once the list has loaded. The param is dropped right
  // after so closing the dialog doesn't reopen it on the next render.
  useEffect(() => {
    if (isDrawerView) return;
    const wanted = searchParams.get("action_item");
    if (!wanted || actionItems.length === 0) return;
    const item = actionItems.find((ai) => String(ai.id) === wanted);
    if (item) {
      setSelectedActionItem(item);
      setIsDialogOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("action_item");
    setSearchParams(next, { replace: true });
  }, [actionItems, searchParams, setSearchParams, isDrawerView]);

  const totalTimeTaken = useMemo(
    () =>
      actionItems.reduce((sum, item) => {
        const time = parseFloat(item.time_taken);
        return isNaN(time) ? sum : sum + time;
      }, 0),
    [actionItems]
  );

  // Only the tracks under the selected project are offered, matching create.
  const filteredTracks = useMemo(
    () =>
      projectFilter
        ? tracks.filter((t) => String(t.project) === String(projectFilter))
        : tracks,
    [tracks, projectFilter]
  );

  const handleFieldChange = useCallback(
    (field, value) => {
      if (field === "epic") {
        setEpicFilter(value);
        setProjectFilter("");
        // The seeding effect above is one-shot, so clearing the project here
        // sticks and the user can pick freely within the new epic.
        return;
      }
      if (field === "project") {
        setProjectFilter(value);
        return;
      }
      const normalized =
        NULLABLE_FKS.includes(field) && value === "" ? null : value;
      setTask((prev) => ({ ...prev, [field]: normalized }));
      patchField(field, normalized);
      // Action-item Types are constrained by the task's category, so a category
      // change has to drop the ones it just invalidated.
      if (field === "category") clearInvalidActionItemTypes(normalized);
    },
    [patchField, clearInvalidActionItemTypes]
  );

  // --- Action items -------------------------------------------------------

  const handleContextMenu = (event, actionItem) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
    setSelectedActionItem(actionItem);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedActionItem(null);
  };

  const handleDeleteActionItem = async () => {
    // Drafts have no id yet but still need removing from the list.
    const key = selectedActionItem?.id ?? selectedActionItem?.tempId;
    if (key == null) return;
    await removeActionItem(key);
    handleCloseContextMenu();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!task) return null;

  const values = {
    ...task,
    // The serializer nests these; the form works in plain ids.
    assignee: task.assignee?.id ?? task.assignee ?? "",
    reviewer: task.reviewer?.id ?? task.reviewer ?? "",
    track: task.track ?? "",
    epic: epicFilter,
    project: projectFilter,
    time_taken_total: totalTimeTaken,
  };
  const errors = Object.fromEntries(
    REQUIRED_FIELDS.filter((f) => !values[f]).map((f) => [f, true])
  );

  // Sits directly under the task name, matching the create dialog.
  const actionItemsSlot = (
    <ActionItemsTable
      actionItems={actionItems}
      category={values.category}
      onUpdateItem={handleActionItemUpdate}
      onAddItem={guestCaps.canManageActionItems ? handleAddActionItem : undefined}
      onOpenItem={(item) => {
        setSelectedActionItem(item);
        setIsDialogOpen(true);
      }}
      onContextMenu={handleContextMenu}
      compact={isDrawerView}
    />
  );

  const headerActions = (
    <>
      <Tooltip title="Copy link to task">
        <IconButton size="small" onClick={handleCopyLink} aria-label="Copy link to task">
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {!isDrawerView && (
        <Tooltip title="Show as drawer">
          <IconButton size="small" onClick={handleShowAsDrawer} aria-label="Show as drawer">
            <ViewSidebar fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <Box sx={{ p: 1, margin: "0 auto" }}>
      {!isDrawerView && (
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={handleGoBack}
          sx={{ mb: 1 }}
        >
          Back
        </Button>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <TaskFormFields
          values={values}
          actionItems={actionItems}
          actionItemsSlot={actionItemsSlot}
          headerActions={headerActions}
          options={{
            epics,
            projects,
            tracks: filteredTracks,
            allTracks: tracks,
            departments,
            divisions,
          }}
          errors={errors}
          onChange={handleFieldChange}
          disabled={!guestCaps.canManageTasks}
          recurrenceSlot={
            <RecurrenceControl
              taskId={taskId}
              workspaceId={currentWorkspace?.id}
              value={task?.recurrence}
              doDate={task?.do_date}
              disabled={!guestCaps.canManageTasks}
              // Attaching a rule can set the do_date and sprint; reload them.
              onSaved={() => refetchTaskRef.current?.()}
            />
          }
          nameActions={
            <Button
              size="small"
              variant="outlined"
              startIcon={<DashboardCustomizeOutlined />}
              onClick={() => setTemplateDialogOpen(true)}
              disabled={!guestCaps.canManageTasks}
              sx={{
                borderRadius: 5,
                textTransform: "none",
                py: 0.25,
                px: 1.25,
                minHeight: 28,
                fontSize: "0.75rem",
              }}
            >
              Template
            </Button>
          }
        />

        <ActionItemTemplateDialog
          open={templateDialogOpen}
          onClose={() => {
            setTemplateDialogOpen(false);
            reloadActionItems();
          }}
          taskId={task.id}
        />
      </Paper>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {guestCaps.canManageActionItems && (
          <MenuItem
            onClick={handleDeleteActionItem}
            sx={{ color: "error.main", "&:hover": { bgcolor: "error.light" } }}
          >
            <Delete sx={{ mr: 1 }} fontSize="small" />
            Delete Action Item
          </MenuItem>
        )}
      </Menu>

      <ActionItemDialog
        open={isDialogOpen}
        actionItemId={selectedActionItem?.id}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedActionItem(null);
          reloadActionItems();
        }}
      />

      <Snackbar
        open={linkCopied}
        autoHideDuration={2000}
        onClose={() => setLinkCopied(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={8000}
        onClose={() => setSaveError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSaveError("")} sx={{ width: "100%" }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskDetailsPage;
